import * as React from "react";
import { Link, useParams } from "react-router-dom";
import { EsimLink } from "@/components/EntityLink";
import { ArrowLeft } from "lucide-react";
import {
  approveOrder,
  closeOrder,
  noteRefund,
  readOrder,
  reissueOrderLink,
  retryOrder,
  type AdminOrderClient,
  type AdminOrderEsimRow,
  type AdminOrderPaymentRow,
  type AdminOrderRecord,
  type AdminOrderTopupRow,
} from "@/api/admin";
import { ORDER_ACTION_FROM, failureLabel, type OrderAction, type OrderStatus } from "@ewn/shared";
import { useAction, useAsync } from "@/lib/useAsync";
import { country, money, when } from "@/lib/format";
import { sourceBadge, statusBadge } from "@/screens/OrdersScreen";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DetailList, DetailRow } from "@/components/ui/detail-row";
import { Field, Textarea } from "@/components/ui/input";
import { Notice, Warnings } from "@/components/ui/feedback";
import { PageHeader } from "@/components/ui/page-header";
import { ConfirmModal } from "@/components/ui/modal";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * One order, and the four things an operator can do to it.
 *
 * EVERY ACTION IS FENCED ON THE SERVER, on the status `ORDER_ACTION_FROM` in
 * `@ewn/shared` names, and this screen offers an action only on that same
 * status. A status is all the screen checks: Close still shows on an order
 * whose coins have arrived, and the server refuses it. The screen can also be
 * stale: a colleague acts first, the button is still showing, and the server
 * answers 409. So the screen reloads after every action, and "somebody got
 * there first" arrives with the new status beside it.
 *
 * Approve and Close are behind a confirmation: one BUYS AN eSIM (real money,
 * from a supplier, no undo), the other ends an order a customer may still be
 * paying. Retry and the refund note are not -- one is idempotent and the other
 * only writes text.
 *
 * THE RESPONSE HAS A SHAPE NOW. This file used to declare
 * `type Row = Record<string, unknown>`, cast `readOrder` twice to reach it, and
 * read every field through a `str()` helper whose comment explained that the
 * worker answered `SELECT *` and so "the shape is wider than anything worth
 * hand-typing here". The worker names its columns; `AdminOrderDetail` in
 * `@ewn/shared` is that list, and a field this screen misspells is a compile
 * error rather than an em dash nobody notices.
 */
export function OrderScreen() {
  const { code = "" } = useParams();
  const { data, loading, error, reload } = useAsync(() => readOrder(code), [code]);
  const action = useAction();
  const [confirmApprove, setConfirmApprove] = React.useState(false);
  const [confirmClose, setConfirmClose] = React.useState(false);
  const [confirmLink, setConfirmLink] = React.useState(false);
  /** The link just issued, for the operator to hand over. */
  const [issued, setIssued] = React.useState<string | null>(null);

  const order = data?.order;

  async function act(fn: () => Promise<{ warnings?: string[] }>) {
    const ok = await action.run(fn);
    // Reloaded whether it worked or not. A refusal is usually "the row moved",
    // and the operator needs to see what it moved to.
    reload();
    return ok;
  }

  return (
    <div className="space-y-4">
      <PageHeader title={code} pretitle="Order">
        <Button variant="outline" size="sm" onClick={() => history.back()}>
          <ArrowLeft aria-hidden className="h-3.5 w-3.5" />
          Back
        </Button>
      </PageHeader>

      {error && <Notice tone="error">{error}</Notice>}
      {action.error && <Notice tone="error">{action.error}</Notice>}
      <Warnings warnings={action.warnings} />

      {loading && !data ? (
        <Card>
          <CardContent className="space-y-3">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </CardContent>
        </Card>
      ) : !data || !order ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No order with that code.{" "}
            <Link to="/orders" className="underline underline-offset-4">
              Back to the ledger
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            <OrderFacts order={order} />
            <WhereFrom client={data.client} />

            {data.esims.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>eSIMs</CardTitle>
                </CardHeader>
                {data.esims.map((esim) => (
                  <EsimFacts key={esim.iccid} esim={esim} />
                ))}
              </Card>
            )}

            {data.topups.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Top-ups</CardTitle>
                </CardHeader>
                {data.topups.map((topup) => (
                  <TopupFacts key={topup.id} topup={topup} />
                ))}
              </Card>
            )}

            {data.payments.length > 0 && (
              <Card>
                <CardHeader>
                  {/* Every attempt, the retired ones too: a coin switch opens a
                      new address and retires the old, and money can still
                      arrive on the old one. */}
                  <CardTitle>Payment attempts</CardTitle>
                </CardHeader>
                {data.payments.map((payment) => (
                  <PaymentFacts key={payment.payment_id} payment={payment} />
                ))}
              </Card>
            )}
          </div>

          <div className="space-y-4">
            <OrderActions
              status={data.order.status}
              busy={action.busy}
              onAct={{
                approve: () => setConfirmApprove(true),
                retry: () => void act(() => retryOrder(code)),
                close: () => setConfirmClose(true),
              }}
            />
            {data.order.status === ORDER_ACTION_FROM.refund && (
              <RefundCard busy={action.busy} onSave={(note) => act(() => noteRefund(code, note))} />
            )}
            <LinkCard busy={action.busy} issued={issued} onIssue={() => setConfirmLink(true)} />
          </div>
        </div>
      )}

      <ConfirmModal
        open={confirmApprove}
        title="Approve and deliver?"
        confirmLabel="Approve and buy the eSIM"
        busy={action.busy}
        onClose={() => setConfirmApprove(false)}
        body={
          <>
            <p>
              Order <span className="font-mono">{code}</span> will be released and an eSIM bought
              from the supplier immediately.
            </p>
            <p className="mt-2">
              That spends real money and cannot be undone. If a colleague has already approved it,
              you will be told so and nothing will be bought twice.
            </p>
          </>
        }
        onConfirm={async () => {
          await act(() => approveOrder(code));
          setConfirmApprove(false);
        }}
      />

      <ConfirmModal
        open={confirmClose}
        title="Close this order?"
        confirmLabel="Close the order"
        busy={action.busy}
        onClose={() => setConfirmClose(false)}
        body={
          <>
            <p>
              Order <span className="font-mono">{code}</span> stops waiting for payment and its
              addresses are retired, as if the customer had pressed Cancel.
            </p>
            <p className="mt-2">
              Refused if coins have already arrived. Coins that arrive later are not lost: they park
              on Payments for review.
            </p>
          </>
        }
        onConfirm={async () => {
          await act(() => closeOrder(code));
          setConfirmClose(false);
        }}
      />

      <ConfirmModal
        open={confirmLink}
        title="Issue a new link?"
        confirmLabel="Issue a new link"
        busy={action.busy}
        onClose={() => setConfirmLink(false)}
        body={
          <>
            <p>
              Every earlier link to order <span className="font-mono">{code}</span> stops working,
              including the one in the customer's emails.
            </p>
            <p className="mt-2">
              Check it is the buyer first: the address that paid and its transaction are on this
              page.
            </p>
          </>
        }
        onConfirm={async () => {
          await act(async () => {
            const answer = await reissueOrderLink(code);
            setIssued(answer.link);
            return answer;
          });
          setConfirmLink(false);
        }}
      />
    </div>
  );
}

/**
 * Where the order was placed from, for telling a customer from an abuser:
 * the country and operator Cloudflare read off the address, and the browser.
 * The address itself is on the customer's screen, not here.
 */
function WhereFrom({ client }: { client: AdminOrderClient }) {
  const known = client.country || client.isp || client.device;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Placed from</CardTitle>
      </CardHeader>
      {known ? (
        <DetailList>
          <DetailRow label="Country">{country(client.country)}</DetailRow>
          <DetailRow label="Network">{client.isp ?? "—"}</DetailRow>
          <DetailRow label="Device">{client.device ?? "—"}</DetailRow>
        </DetailList>
      ) : (
        <CardContent className="text-sm text-muted-foreground">
          Not known: cleared once the order passed the retention window, or never recorded.
        </CardContent>
      )}
    </Card>
  );
}

function OrderFacts({ order }: { order: AdminOrderRecord }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Order</CardTitle>
        <div className="flex gap-1.5">
          {order.source === "telegram" && sourceBadge(order.source)}
          {statusBadge(order.status)}
        </div>
      </CardHeader>
      <DetailList>
        <DetailRow label="Kind">{order.kind}</DetailRow>
        <DetailRow label="Package">{order.package_id}</DetailRow>
        <DetailRow label="Quantity">{order.quantity}</DetailRow>
        <DetailRow label="Total">{money(order.total_cents)}</DetailRow>
        <DetailRow label="Discount">{order.discount_pct}%</DetailRow>
        {order.coupon_code && (
          <DetailRow label="Coupon" mono>
            {order.coupon_code}
          </DetailRow>
        )}
        <DetailRow label="Source">{sourceBadge(order.source)}</DetailRow>
        <DetailRow label="Customer">{order.email ?? "—"}</DetailRow>
        <DetailRow label="Created">{when(order.created_at)}</DetailRow>
        <DetailRow label="Paid">{when(order.paid_at)}</DetailRow>
        {order.held_at && <DetailRow label="Held since">{when(order.held_at)}</DetailRow>}
        {order.failed_at && <DetailRow label="Failed">{when(order.failed_at)}</DetailRow>}
        {/* The reason in words, whenever there is one -- a paid order waiting on
            the supplier carries one too, without having failed. */}
        {order.failure_code && (
          <DetailRow label="Reason">{failureLabel(order.failure_code)}</DetailRow>
        )}
        {order.failure_message && <DetailRow label="Detail">{order.failure_message}</DetailRow>}
        <DetailRow label="Supplier attempts">{order.provision_attempts}</DetailRow>
        {order.esimgo_order_ref && (
          <DetailRow label="Supplier reference" mono>
            {order.esimgo_order_ref}
          </DetailRow>
        )}
        {order.refund_note && <DetailRow label="Refund note">{order.refund_note}</DetailRow>}
      </DetailList>
    </Card>
  );
}

function EsimFacts({ esim }: { esim: AdminOrderEsimRow }) {
  return (
    <DetailList>
      <DetailRow label="ICCID" mono>
        <EsimLink iccid={esim.iccid} />
      </DetailRow>
      {/* THE NUMBER IS THE PRODUCT, and the supplier assigns it some time after
          the profile exists -- so "not yet assigned" is a real state with a
          cron job behind it, not a field somebody forgot to fill in. */}
      <DetailRow label="Number">{esim.msisdn ?? "not yet assigned"}</DetailRow>
      <DetailRow label="Profile">{esim.profile_state ?? "—"}</DetailRow>
      <DetailRow label="Bundle">{esim.last_bundle_state ?? "—"}</DetailRow>
      <DetailRow label="Bundle expires">{when(esim.bundle_expires_at)}</DetailRow>
      {/* The LPA string is the eSIM itself. Shown, because a support call
          sometimes needs it re-sent, and in monospace so a 100-character
          string does not blow the layout out. */}
      <DetailRow label="LPA" mono>
        {esim.lpa}
      </DetailRow>
    </DetailList>
  );
}

function TopupFacts({ topup }: { topup: AdminOrderTopupRow }) {
  return (
    <DetailList>
      <DetailRow label="Package">{topup.package_id}</DetailRow>
      <DetailRow label="Price">{money(topup.price_cents)}</DetailRow>
      <DetailRow label="Status">{topup.status}</DetailRow>
      <DetailRow label="Line (ICCID)" mono>
        <EsimLink iccid={topup.iccid} />
      </DetailRow>
      {topup.esimgo_order_ref && (
        <DetailRow label="Supplier reference" mono>
          {topup.esimgo_order_ref}
        </DetailRow>
      )}
      <DetailRow label="Created">{when(topup.created_at)}</DetailRow>
    </DetailList>
  );
}

function PaymentFacts({ payment }: { payment: AdminOrderPaymentRow }) {
  return (
    <DetailList>
      {/* Ticker AND chain: three enabled coins share a ticker, so the ticker
          alone does not say which one arrived. */}
      <DetailRow label="Coin">
        {payment.ticker} ({payment.network_label})
      </DetailRow>
      <DetailRow label="Status">
        {payment.status}
        {payment.provider_status ? ` — ${payment.provider_status}` : ""}
      </DetailRow>
      <DetailRow label="Asked for">{payment.pay_amount}</DetailRow>
      <DetailRow label="Actually paid">{payment.actually_paid}</DetailRow>
      <DetailRow label="Settled">{when(payment.settled_at)}</DetailRow>
      {payment.superseded_at && (
        <DetailRow label="Retired">
          {when(payment.superseded_at)} (coin switched or cancelled)
        </DetailRow>
      )}
      {payment.excess_amount && (
        <DetailRow label="Overpaid by">
          {payment.excess_amount} {payment.ticker}
          {payment.excess_refunded_at ? ` — refunded ${when(payment.excess_refunded_at)}` : " — owed back"}
        </DetailRow>
      )}
      {payment.manual_review_at && (
        <DetailRow label="Flagged for review">{when(payment.manual_review_at)}</DetailRow>
      )}
      {/* Who answered for it, and what they found. Both null until somebody
          closes the review; see `domain/admin-payments.ts` for why closing it
          does not hand the payment back to the sweeper. */}
      {payment.reviewed_by && <DetailRow label="Closed by">{payment.reviewed_by}</DetailRow>}
      {payment.review_note && <DetailRow label="Review note">{payment.review_note}</DetailRow>}
      <DetailRow label="Address" mono>
        {payment.pay_address}
      </DetailRow>
    </DetailList>
  );
}

type ButtonAction = Exclude<OrderAction, "refund">;

/** The Actions card's buttons, one per action; the refund has its own card. */
const BUTTONS: Record<
  ButtonAction,
  { label: string; hint: string; variant: React.ComponentProps<typeof Button>["variant"] }
> = {
  approve: {
    label: "Approve and deliver",
    hint: "Releases a held order. This buys an eSIM from the supplier — real money, no undo.",
    variant: "default",
  },
  retry: {
    label: "Ask the supplier again",
    hint: "For an order the supplier has not delivered. The attempt counter is not reset.",
    variant: "secondary",
  },
  close: {
    label: "Close the order",
    hint: "For an order still waiting for payment. Refused once coins have arrived.",
    variant: "outline",
  },
};

/**
 * The buttons this order's status allows (`ORDER_ACTION_FROM`), and no card
 * when it allows none.
 */
function OrderActions({
  status,
  busy,
  onAct,
}: {
  status: OrderStatus;
  busy: boolean;
  onAct: Record<ButtonAction, () => void>;
}) {
  const offered = (Object.keys(BUTTONS) as ButtonAction[]).filter(
    (action) => ORDER_ACTION_FROM[action] === status,
  );
  if (offered.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Actions</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {offered.map((action) => (
          <div key={action} className="space-y-1.5">
            <Button
              variant={BUTTONS[action].variant}
              className="w-full"
              disabled={busy}
              onClick={onAct[action]}
            >
              {BUTTONS[action].label}
            </Button>
            <p className="text-xs text-muted-foreground">{BUTTONS[action].hint}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/**
 * The customer's way back to the order: a new link, for a buyer who lost
 * theirs or whose link reached someone else. The code alone shows only the
 * order's status (`worker/src/lib/link-key.ts`).
 */
function LinkCard({
  busy,
  issued,
  onIssue,
}: {
  busy: boolean;
  issued: string | null;
  onIssue: () => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Customer link</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          The order code alone shows only the status. A customer who lost the link gets a new one
          from here.
        </p>
        {issued && (
          <code className="block break-all rounded bg-muted px-2 py-1.5 font-mono text-xs">
            {issued}
          </code>
        )}
        <Button variant="secondary" className="w-full" disabled={busy} onClick={onIssue}>
          Issue a new link
        </Button>
      </CardContent>
    </Card>
  );
}

function RefundCard({ busy, onSave }: { busy: boolean; onSave: (note: string) => Promise<boolean> }) {
  const [note, setNote] = React.useState("");

  return (
    <Card>
      <CardHeader>
        <CardTitle>Record a refund</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Field
          label="What happened"
          htmlFor="refund-note"
          hint="This moves no money; it writes down a refund made by hand. Write the transaction, not the address."
        >
          <Textarea
            id="refund-note"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Refunded 0.0041 BTC, tx 9f2c…, 2026-09-20."
          />
        </Field>
        <Button
          variant="secondary"
          className="w-full"
          disabled={busy || note.trim().length === 0}
          onClick={async () => {
            // Cleared only on success: a refusal means nothing was written, and
            // throwing away what was typed would make the operator type it again.
            if (await onSave(note)) setNote("");
          }}
        >
          Mark as refunded
        </Button>
      </CardContent>
    </Card>
  );
}
